import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert,
  Stack, TextField, Select, MenuItem, FormControl,
  IconButton, Avatar, Divider, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, InputAdornment, useMediaQuery,
  Pagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Refresh, Search, History } from '@mui/icons-material';
import api from '../../services/api';

interface Transacao {
  ID: string;
  REFERENCIA: string;
  ENTIDADE_CODIGO: string;
  MONTANTE: number;
  MONTANTE_COBRADO: number;
  COMISSAO_VALOR: number;
  ESTADO: string;
  DT_TRANSACAO: string;
  faturaNumero: string | null;
}

const ESTADO_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  PENDENTE: 'warning',
  PROCESSADO: 'success',
  FALHOU: 'error',
  ESTORNADO: 'default',
};

const ESTADO_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  PROCESSADO: 'Processado',
  FALHOU: 'Falhou',
  ESTORNADO: 'Estornado',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const fmtDate = (dt: string) => {
  const d = new Date(dt);
  return d.toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ENTITY_COLORS: string[] = [
  '#1565C0', '#2E7D32', '#6A1B9A', '#C62828', '#E65100',
  '#00695C', '#283593', '#AD1457',
];

const getEntityColor = (code: string): string => {
  let hash = 0;
  for (let i = 0; i < code.length; i++) hash = code.charCodeAt(i) + ((hash << 5) - hash);
  return ENTITY_COLORS[Math.abs(hash) % ENTITY_COLORS.length];
};

const PAGE_SIZE = 20;

export default function AgenteTransacoes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estadoFilter, setEstadoFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dtInicio, setDtInicio] = useState('');
  const [dtFim, setDtFim] = useState('');

  const fetchTransacoes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (estadoFilter) params.estado = estadoFilter;
      if (search) params.search = search;
      if (dtInicio) params.dtInicio = dtInicio;
      if (dtFim) params.dtFim = dtFim;
      const res = await api.get('/agente/transacoes', { params });
      setTransacoes(res.data?.data || []);
      setTotal(res.data?.total || res.data?.data?.length || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar transacções');
    } finally {
      setLoading(false);
    }
  }, [estadoFilter, search, dtInicio, dtFim, page]);

  useEffect(() => { fetchTransacoes(); }, [fetchTransacoes]);

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleFilterChange = (field: string, value: string) => {
    setPage(1);
    if (field === 'estado') setEstadoFilter(value);
    if (field === 'dtInicio') setDtInicio(value);
    if (field === 'dtFim') setDtFim(value);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showing = Math.min(transacoes.length, total);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Page header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={800}>
              Minhas Transacções
            </Typography>
            {total > 0 && (
              <Chip
                label={total}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, height: 22 }}
              />
            )}
          </Stack>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              A mostrar {showing} de {total} transacção{total !== 1 ? 'ões' : ''}
            </Typography>
          )}
        </Box>
        <IconButton onClick={fetchTransacoes} disabled={loading} color="primary">
          <Refresh />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 2.5, borderRadius: 2 }}>
        <CardContent sx={{ p: '12px 16px !important' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            flexWrap="wrap"
            alignItems={{ sm: 'center' }}
          >
            <TextField
              size="small"
              placeholder="Pesquisar referência…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={estadoFilter}
                displayEmpty
                onChange={e => handleFilterChange('estado', e.target.value)}
              >
                <MenuItem value="">Todos os estados</MenuItem>
                {Object.entries(ESTADO_LABEL).map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="De"
              value={dtInicio}
              onChange={e => handleFilterChange('dtInicio', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              type="date"
              label="Até"
              value={dtFim}
              onChange={e => handleFilterChange('dtFim', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : transacoes.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <History sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              Nenhuma transacção encontrada
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {estadoFilter || search || dtInicio || dtFim
                ? 'Tente ajustar os filtros de pesquisa.'
                : 'As suas transacções aparecerão aqui após o primeiro pagamento processado.'}
            </Typography>
          </Box>
        </Card>
      ) : isMobile ? (
        /* ─── Mobile: card list ─── */
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          {transacoes.map((t, idx) => (
            <React.Fragment key={t.ID}>
              {idx > 0 && <Divider />}
              <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 42,
                    height: 42,
                    bgcolor: getEntityColor(t.ENTIDADE_CODIGO),
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {t.ENTIDADE_CODIGO.substring(0, 2)}
                </Avatar>

                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={700} noWrap fontFamily="monospace">
                    {t.ENTIDADE_CODIGO}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    Ref: {t.REFERENCIA}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {fmtDate(t.DT_TRANSACAO)}
                  </Typography>
                  {t.faturaNumero && (
                    <Chip
                      label={t.faturaNumero}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                    />
                  )}
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body2" fontWeight={800}>
                    {fmt(t.MONTANTE)}
                  </Typography>
                  <Chip
                    label={`+${fmt(t.COMISSAO_VALOR)}`}
                    size="small"
                    sx={{
                      my: 0.25,
                      bgcolor: 'success.50',
                      color: 'success.main',
                      fontWeight: 700,
                      fontSize: 10,
                      height: 18,
                    }}
                  />
                  <Box>
                    <Chip
                      label={ESTADO_LABEL[t.ESTADO] || t.ESTADO}
                      size="small"
                      color={ESTADO_COLOR[t.ESTADO] || 'default'}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  </Box>
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </Card>
      ) : (
        /* ─── Desktop: table ─── */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Entidade</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Referência</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Montante</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Cobrado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Comissão</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Factura</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transacoes.map(t => (
                <TableRow key={t.ID} hover>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(t.DT_TRANSACAO)}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 26,
                          height: 26,
                          bgcolor: getEntityColor(t.ENTIDADE_CODIGO),
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        {t.ENTIDADE_CODIGO.substring(0, 2)}
                      </Avatar>
                      <Typography variant="caption" fontFamily="monospace" fontWeight={600}>
                        {t.ENTIDADE_CODIGO}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{t.REFERENCIA}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(t.MONTANTE)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(t.MONTANTE_COBRADO)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="success.main" fontWeight={700}>
                      +{fmt(t.COMISSAO_VALOR)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    {t.faturaNumero
                      ? <Chip label={t.faturaNumero} size="small" color="primary" variant="outlined" sx={{ fontSize: 10 }} />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ESTADO_LABEL[t.ESTADO] || t.ESTADO}
                      size="small"
                      color={ESTADO_COLOR[t.ESTADO] || 'default'}
                      sx={{ fontSize: 11 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {pageCount > 1 && !loading && (
        <Stack direction="row" justifyContent="center" mt={3}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}
    </Box>
  );
}
